import { useEffect } from "react";
import flatten from "lodash/flatten";
import qs from "query-string";
import { useInfiniteQuery, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import type { ApiError } from "@circle-react/config/CustomErrors";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import { useRouterQueryParams } from "@circle-react/hooks/useRouterQueryParams";
import type { PaginationHelper } from "@circle-react/types/InternalAPIHelper";
import type { SearchSpace } from "@circle-react/types/Search/Space";
import type { FilterType } from "./types";

const REQUEST_KEY = ["courses", "directory"];
const SUPPORTED_FILTERS: FilterType[] = ["all", "joined"];

function isFilterSupported(filter: unknown): filter is FilterType {
  return (
    typeof filter === "string" && SUPPORTED_FILTERS.some(f => f === filter)
  );
}

export const useInvalidateCoursesInfiniteQuery = () => {
  const queryClient = useQueryClient();

  return {
    invalidateCourseQueries: () => queryClient.invalidateQueries(REQUEST_KEY),
  };
};

export const useCoursesInfiniteQuery = () => {
  const { filter: filterParam } = useRouterQueryParams();
  const { topics } = useRouterQueryParams();
  const history = useHistory();

  const filter: FilterType = isFilterSupported(filterParam)
    ? filterParam
    : "all";

  const fetchPage = ({ pageParam = 1 }) => {
    // ToDo: We need to refactor the pagination method on the backend to take in count
    // only the spaces that are visible to the user when calculating the total count.
    // Sending 20 spaces for now is enough to fix the issue.
    const params = {
      page: pageParam,
      space_type: "course",
      sort: "directory",
      per_page: 30,
      only_joined: filter === "joined",
      topics: topics || [],
    };

    return reactQueryGet(
      internalApi.search.spaces.index({
        params,
      }),
    );
  };

  const result = useInfiniteQuery<PaginationHelper<SearchSpace>, ApiError>(
    [...REQUEST_KEY, filter, topics],
    fetchPage,
    {
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    },
  );

  const handleFilterClick = (filterValue: FilterType) => {
    history.push({ search: qs.stringify({ filter: filterValue }) });
  };

  const pages = result.data?.pages || [];
  const page = pages.length > 0 ? pages[pages.length - 1] : null;
  const pageNumber = page?.page;
  const isPageEmpty = page?.records.length === 0;
  const hasNextPage = Boolean(page?.has_next_page);
  const { isFetchingNextPage, fetchNextPage } = result;

  // A temporary workaround to auto-fetch the next page if backend returns an
  // empty page due to additional, policy-related filtering after fetching
  // records from the database. This fixes issues with the infinite scroll
  // getting stuck and not fetching the next page.
  useEffect(() => {
    if (isFetchingNextPage || !isPageEmpty || !hasNextPage) return;

    void fetchNextPage();
  }, [pageNumber, isFetchingNextPage, isPageEmpty, hasNextPage, fetchNextPage]);

  const dataLength = pages[0]?.count || 0;
  const courses = flatten(pages.map(page => page.records));

  return {
    ...result,
    dataLength,
    courses,
    filter,
    handleFilterClick,
  };
};
